/**
 * Laboratorul soft-body (Etapa 4): caroseria node-beam a mașinii alese, cu modelul 3D legat de noduri pe placa
 * video, grinzile colorate după tensiune și testele: parcare, cădere, impact frontal în perete, torsiune.
 */
import * as THREE from 'three/webgpu';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { VEHICLES } from '../data/catalog';
import type { VehicleDefinition } from '../data/types';
import { createLoader, type ModelSource } from '../cars/loader';
import { loadConceptScene, nodeMaterial, shapeConcept } from '../cars/concept';
import { bindVertices, nodeTexture, skinnedPosition, writeNodes } from '../lab/skin';
import type { LabCmd, LabMsg } from '../worker/body.worker';
import { brandLogo } from './logos';

type Level = 'mobile' | 'desktop';
const LEVEL_KEY = 'nb-level';
const BODIES = import.meta.glob('../../data/bodies/*.json', { query: '?raw', import: 'default' }) as Record<string, () => Promise<string>>;

export interface LabOptions { models: ModelSource; onClose?: () => void; /** în pagina de pe claude.ai worker-ul e inclus în pagină */ createWorker?: () => Worker }

const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] as string);

async function bodyJson(id: string, level: Level): Promise<string> {
  const key = Object.keys(BODIES).find((k) => k.endsWith(`/${id}-${level}.json`));
  if (!key) throw new Error(`lipsește structura ${id}-${level}`);
  return BODIES[key]();
}

export function createLab(root: HTMLElement, opts: LabOptions) {
  let worker: Worker | null = null;
  let renderer: THREE.WebGPURenderer | null = null;
  let scene: THREE.Scene, camera: THREE.PerspectiveCamera, controls: OrbitControls;
  let ro: ResizeObserver | null = null;
  let car: VehicleDefinition = VEHICLES[0];
  let level: Level | null = null;
  let nodes = 0, beams = 0, rest: Float32Array = new Float32Array(0), latest: Float32Array | null = null;
  let sab: { seq: Int32Array; data: Float32Array } | null = null;
  let pairs: Uint32Array = new Uint32Array(0), strain: Float32Array = new Float32Array(0), broken: Uint8Array = new Uint8Array(0);
  let groups: string[] = [];
  let curTex: THREE.DataTexture | null = null, restTex: THREE.DataTexture | null = null;
  let carGroup: THREE.Group | null = null, nodeMesh: THREE.InstancedMesh | null = null, beamLines: THREE.LineSegments | null = null, wall: THREE.Mesh | null = null;
  const show = { model: true, nodes: false, beams: true };
  let stats = { stepsPerSec: 0, usPerStep: 0, broken: 0, plastic: 0, slow: false };
  let info = { mass: 0, problems: 0, torsion: '', bench: '', status: '' };
  let slowmo = 1;
  const loader = createLoader();

  root.innerHTML = `<header class="g-head"><h2>Crash test</h2><span class="muted">caroserie node-beam · Rust/WASM · 2000 Hz</span><button class="icon" data-close aria-label="Închide">✕</button></header>
    <div class="lab-body"><aside class="lab-side"></aside><div class="lab-view"><div class="lab-canvas"></div><div class="lab-stats"></div></div></div>`;
  const side = root.querySelector('.lab-side') as HTMLElement;
  const view = root.querySelector('.lab-canvas') as HTMLElement;
  const statsEl = root.querySelector('.lab-stats') as HTMLElement;

  const send = (m: LabCmd) => worker?.postMessage(m);

  function panel(): void {
    const btn = (a: string, label: string, on = false) => `<button data-a="${a}" aria-pressed="${on}">${label}</button>`;
    side.innerHTML = `
      <div class="lab-cars">${VEHICLES.map((v) => `<button data-car="${v.id}" aria-pressed="${v.id === car.id}"><span class="logo sm">${brandLogo(v.brandId)}</span><span>${esc(v.model)} <small class="muted">${esc(v.variant)}</small></span></button>`).join('')}</div>
      <div class="lab-row"><span class="muted">Nivel</span><span class="seg">${btn('level:auto', 'Auto', !localStorage.getItem(LEVEL_KEY))}${btn('level:mobile', 'Mobil', localStorage.getItem(LEVEL_KEY) === 'mobile')}${btn('level:desktop', 'Desktop', localStorage.getItem(LEVEL_KEY) === 'desktop')}</span></div>
      <div class="lab-row">${btn('park', 'Parcare')}${btn('drop1', 'Cădere 1 m')}${btn('drop3', 'Cădere 3 m')}</div>
      <div class="lab-row">${btn('crash50', 'Impact 50 km/h')}${btn('crash30', 'Impact 30 km/h')}</div>
      <div class="lab-row">${btn('torsion', 'Test torsiune')}</div>
      <div class="lab-row"><span class="muted">Timp</span><span class="seg">${btn('slow:1', '1×', slowmo === 1)}${btn('slow:0.25', '¼×', slowmo === 0.25)}${btn('slow:0.05', '1/20×', slowmo === 0.05)}</span></div>
      <div class="lab-row"><span class="muted">Arată</span>${btn('show:model', 'Model', show.model)}${btn('show:beams', 'Grinzi', show.beams)}${btn('show:nodes', 'Noduri', show.nodes)}</div>
      <p class="muted small">Grinzile: verde = fără efort, galben → roșu = aproape de curgere sau dincolo; cele rupte dispar. Roțile sunt provizorii (Etapa 5 aduce suspensia și anvelopele).</p>`;
  }

  function statsHtml(): string {
    return `<b>${esc(car.model)} ${esc(car.variant)}</b> · ${level === 'desktop' ? 'Desktop' : 'Mobil'} · ${nodes} noduri · ${beams} grinzi · ${info.mass.toFixed(0)} kg<br>
      ${Math.round(stats.stepsPerSec)} pași/s · ${stats.usPerStep.toFixed(0)} µs/pas${stats.slow ? ' · <span class="bad">dispozitivul nu ține pasul: simularea încetinește</span>' : ''}<br>
      rupte ${stats.broken} · deformate plastic ${stats.plastic}${info.problems ? ` · <span class="bad">${info.problems} probleme</span>` : ' · <span class="ok">✓ masă, centru de greutate, stabilitate</span>'}
      ${info.torsion ? `<br>${info.torsion}` : ''}${info.bench ? `<br><span class="muted">${info.bench}</span>` : ''}${info.status ? `<br><span class="muted">${info.status}</span>` : ''}`;
  }

  async function chooseLevel(): Promise<Level> {
    const saved = localStorage.getItem(LEVEL_KEY);
    if (saved === 'mobile' || saved === 'desktop') return saved;
    info.status = 'Benchmark de 3 secunde pentru nivelul de detaliu…';
    const json = await bodyJson('wendland-pendler-19tdp', 'desktop');
    const rate = await new Promise<number>((resolve) => {
      const h = (e: MessageEvent<LabMsg>) => { if (e.data.type === 'result' && e.data.kind === 'bench') { worker!.removeEventListener('message', h); resolve(e.data.value); } };
      worker!.addEventListener('message', h);
      send({ type: 'bench', json, seconds: 3 });
    });
    // desktop doar dacă rulează de cel puțin 1,5 ori mai repede decât timpul real
    const lv: Level = rate >= 3000 ? 'desktop' : 'mobile';
    info.bench = `Benchmark: nivelul desktop rulează ${Math.round(rate)} pași/s (timpul real cere 2000) → ${lv === 'desktop' ? 'Desktop' : 'Mobil'}`;
    info.status = '';
    return lv;
  }

  function clearCar(): void {
    for (const o of [carGroup, nodeMesh, beamLines]) {
      if (!o) continue;
      o.traverse((c) => { const m = c as THREE.Mesh; m.geometry?.dispose(); const mat = m.material as THREE.Material | THREE.Material[] | undefined; (Array.isArray(mat) ? mat : mat ? [mat] : []).forEach((x) => x.dispose()); });
      o.removeFromParent();
    }
    carGroup = null; nodeMesh = null; beamLines = null;
    curTex?.dispose(); restTex?.dispose();
  }

  async function loadBody(): Promise<void> {
    if (!level) level = await chooseLevel();
    info.status = 'Se încarcă structura și modelul…';
    statsEl.innerHTML = statsHtml();
    const json = await bodyJson(car.id, level);
    const def = JSON.parse(json) as { nodes: Array<{ pos: number[]; group: string; tag: string }> };
    clearCar();
    nodes = def.nodes.length;
    groups = def.nodes.map((n) => n.group);
    rest = new Float32Array(nodes * 3);
    def.nodes.forEach((n, i) => rest.set(n.pos, i * 3));
    latest = rest.slice();
    const iso = typeof SharedArrayBuffer !== 'undefined' && self.crossOriginIsolated;
    const buf = iso ? new SharedArrayBuffer(16 + nodes * 6 * 4) : undefined;
    sab = buf ? { seq: new Int32Array(buf, 0, 1), data: new Float32Array(buf, 16, nodes * 6) } : null;
    send({ type: 'load', json, sab: buf });

    // modelul: varianta potrivită nivelului, legat de nodurile caroseriei
    curTex = nodeTexture(nodes, rest);
    restTex = nodeTexture(nodes, rest);
    // modelul detaliat, remodelat după proporțiile mașinii; roțile urmează butucul lor, caroseria cele mai apropiate 4 noduri
    const parts = shapeConcept(await loadConceptScene(loader), car);
    const bodyCand = def.nodes.map((n, i) => (n.group === 'wheel' ? -1 : i)).filter((i) => i >= 0);
    const hubs = def.nodes.map((n, i) => (n.tag === 'hub' ? i : -1)).filter((i) => i >= 0);
    const posNode = skinnedPosition(curTex, restTex);
    const mats = new Map<THREE.Material, THREE.MeshPhysicalNodeMaterial>();
    carGroup = new THREE.Group();
    for (const part of parts) {
      const g = part.geometry;
      if (part.wheel >= 0) {
        // geometria roții e centrată pe butuc: o mutăm la butucul din fizică și o legăm doar de el
        const h = hubs[part.wheel];
        g.translate(rest[h * 3], rest[h * 3 + 1], rest[h * 3 + 2]);
      }
      const p = g.getAttribute('position').array as Float32Array;
      const b = part.wheel >= 0 ? bindVertices(p, rest, [hubs[part.wheel]], 1) : bindVertices(p, rest, bodyCand, 4);
      g.setAttribute('nbIndex', new THREE.BufferAttribute(b.index, 4));
      g.setAttribute('nbWeight', new THREE.BufferAttribute(b.weight, 4));
      let m = mats.get(part.material);
      if (!m) { m = nodeMaterial(part.material, car.design.colors[0].hex, posNode); mats.set(part.material, m); }
      const sm = new THREE.Mesh(g, m);
      sm.castShadow = true;
      sm.frustumCulled = false;
      carGroup.add(sm);
    }
    carGroup.visible = show.model;
    scene.add(carGroup);

    // nodurile și grinzile pentru depanare
    nodeMesh = new THREE.InstancedMesh(new THREE.IcosahedronGeometry(level === 'desktop' ? 0.018 : 0.024, 0), new THREE.MeshBasicMaterial(), nodes);
    const col = new THREE.Color();
    for (let i = 0; i < nodes; i++) nodeMesh.setColorAt(i, col.set(groups[i] === 'engine' ? 0xff9a3d : groups[i] === 'wheel' ? 0x5aa9ff : 0xffffff));
    nodeMesh.frustumCulled = false;
    nodeMesh.visible = show.nodes;
    scene.add(nodeMesh);
    const L = car.dimensions.lengthMm / 1000;
    camera.position.set(L * 0.85, 1.7, L * 0.95);
    controls.target.set(0, 0.6, 0);
    info.status = '';
  }

  function buildBeams(): void {
    beamLines?.removeFromParent();
    beamLines?.geometry.dispose();
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(beams * 6), 3));
    g.setAttribute('color', new THREE.BufferAttribute(new Float32Array(beams * 6), 3));
    beamLines = new THREE.LineSegments(g, new THREE.LineBasicMaterial({ vertexColors: true, transparent: true, opacity: 0.9 }));
    beamLines.frustumCulled = false;
    beamLines.visible = show.beams;
    scene.add(beamLines);
  }

  function onMsg(e: MessageEvent<LabMsg>): void {
    const m = e.data;
    if (m.type === 'loaded') {
      beams = m.beams; pairs = m.pairs; strain = new Float32Array(beams); broken = new Uint8Array(beams);
      info.mass = m.mass; info.problems = m.problems.filter((p) => p.severity === 'error').length; info.torsion = '';
      buildBeams();
    } else if (m.type === 'frame') latest = m.positions;
    else if (m.type === 'beams') { strain = m.strain; broken = m.broken; }
    else if (m.type === 'stats') stats = m;
    else if (m.type === 'wall') {
      wall?.removeFromParent();
      wall = null;
      if (m.z !== null) {
        wall = new THREE.Mesh(new THREE.BoxGeometry(8, 5, 0.4), new THREE.MeshStandardMaterial({ color: 0x8a8f99, roughness: 0.9 }));
        wall.position.set(0, 2.0, m.z + 0.2);
        wall.receiveShadow = true;
        scene.add(wall);
        // cadru din lateral, cu peretele și fața mașinii în imagine
        camera.position.set(5.5, 2.0, m.z - 2.6);
        controls.target.set(0, 0.7, m.z - 1.6);
      }
    } else if (m.type === 'result' && m.kind === 'torsion') {
      const target = { 'wendland-pendler-19tdp': 17000, 'wendland-pendler-sprint-20t': 20000, 'rheinhardt-falke-s': 22000, 'kazemoto-hayate-30': 18000, 'zarya-sokol-1500': 7000, 'stallard-mesa-20crd': 21000 }[car.id] ?? 0;
      info.torsion = `Torsiune: <b>${Math.round(m.value).toLocaleString('ro-RO')} N·m/°</b> (ținta ${target.toLocaleString('ro-RO')}) — spatele fixat în prinderile suspensiei, cuplu de 600 N·m în față`;
    } else if (m.type === 'error') info.status = `Eroare: ${esc(m.message)}`;
  }

  function strainColor(s: number, out: Float32Array, o: number): void {
    // 0 → verde, 1,5% → galben, ≥3% → roșu (ordinul deformației de curgere)
    const t = Math.min(1, Math.abs(s) / 0.03);
    const r = t < 0.5 ? t * 2 : 1, g = t < 0.5 ? 1 : 2 - t * 2;
    out[o] = r * 0.95; out[o + 1] = g * 0.85 + 0.1; out[o + 2] = 0.15;
  }

  function frame(): void {
    controls.update();
    let p = latest;
    if (sab) {
      const seq = Atomics.load(sab.seq, 0);
      if (seq > 0) p = sab.data.subarray((seq & 1) * nodes * 3, (seq & 1) * nodes * 3 + nodes * 3);
    }
    if (p && curTex) {
      writeNodes(curTex.image.data as Float32Array, p, nodes);
      curTex.needsUpdate = true;
      if (nodeMesh?.visible) {
        const mtx = new THREE.Matrix4();
        for (let i = 0; i < nodes; i++) { mtx.makeTranslation(p[i * 3], p[i * 3 + 1], p[i * 3 + 2]); nodeMesh.setMatrixAt(i, mtx); }
        nodeMesh.instanceMatrix.needsUpdate = true;
      }
      if (beamLines?.visible && pairs.length) {
        const pos = beamLines.geometry.getAttribute('position') as THREE.BufferAttribute, colA = beamLines.geometry.getAttribute('color') as THREE.BufferAttribute;
        const pa = pos.array as Float32Array, ca = colA.array as Float32Array;
        for (let j = 0; j < beams; j++) {
          const a = pairs[j * 2], b = broken[j] ? a : pairs[j * 2 + 1];
          pa[j * 6] = p[a * 3]; pa[j * 6 + 1] = p[a * 3 + 1]; pa[j * 6 + 2] = p[a * 3 + 2];
          pa[j * 6 + 3] = p[b * 3]; pa[j * 6 + 4] = p[b * 3 + 1]; pa[j * 6 + 5] = p[b * 3 + 2];
          strainColor(strain[j] ?? 0, ca, j * 6);
          strainColor(strain[j] ?? 0, ca, j * 6 + 3);
        }
        pos.needsUpdate = true; colA.needsUpdate = true;
      }
    }
    renderer!.render(scene, camera);
  }

  let statsTimer = 0;
  async function open(): Promise<void> {
    root.hidden = false;
    panel();
    worker = opts.createWorker ? opts.createWorker() : new Worker(new URL('../worker/body.worker.ts', import.meta.url), { type: 'module' });
    worker.addEventListener('message', onMsg);
    renderer = new THREE.WebGPURenderer({ antialias: true });
    renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
    renderer.shadowMap.enabled = true;
    view.appendChild(renderer.domElement);
    scene = new THREE.Scene();
    scene.background = new THREE.Color(0x1a2029);
    camera = new THREE.PerspectiveCamera(45, 1, 0.05, 200);
    controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.maxPolarAngle = Math.PI * 0.49;
    scene.add(new THREE.HemisphereLight(0xdfe8ff, 0x3a3630, 1.3));
    const sun = new THREE.DirectionalLight(0xffffff, 2.2);
    sun.position.set(6, 9, 4);
    sun.castShadow = true;
    sun.shadow.mapSize.set(1024, 1024);
    sun.shadow.camera.left = -8; sun.shadow.camera.right = 8; sun.shadow.camera.top = 8; sun.shadow.camera.bottom = -8;
    scene.add(sun);
    const ground = new THREE.Mesh(new THREE.PlaneGeometry(80, 80), new THREE.MeshStandardMaterial({ color: 0x2b3038, roughness: 0.95 }));
    ground.rotation.x = -Math.PI / 2;
    ground.receiveShadow = true;
    scene.add(ground);
    const grid = new THREE.GridHelper(80, 80, 0x3d4652, 0x333a44);
    grid.position.y = 0.002;
    scene.add(grid);
    const resize = () => {
      const w = view.clientWidth, h = view.clientHeight;
      if (w < 2 || h < 2) return;
      renderer!.setSize(w, h);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };
    ro = new ResizeObserver(resize);
    ro.observe(view);
    await renderer.init();
    resize();
    renderer.setAnimationLoop(frame);
    statsTimer = window.setInterval(() => { statsEl.innerHTML = statsHtml(); }, 400);
    try { await loadBody(); } catch (e) { info.status = `Eroare: ${esc(String(e))}`; }
  }

  function close(): void {
    root.hidden = true;
    window.clearInterval(statsTimer);
    renderer?.setAnimationLoop(null);
    clearCar();
    wall?.removeFromParent();
    ro?.disconnect();
    renderer?.dispose();
    renderer?.domElement.remove();
    renderer = null;
    worker?.terminate();
    worker = null;
    opts.onClose?.();
  }

  root.addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    if (t.closest('[data-close]')) { close(); return; }
    const c = t.closest('[data-car]') as HTMLElement | null;
    if (c) { car = VEHICLES.find((v) => v.id === c.dataset.car)!; info.torsion = ''; panel(); void loadBody().catch((err) => { info.status = `Eroare: ${esc(String(err))}`; }); return; }
    const b = t.closest('[data-a]') as HTMLElement | null;
    if (!b) return;
    const [a, arg] = (b.dataset.a ?? '').split(':');
    if (a === 'level') {
      if (arg === 'auto') localStorage.removeItem(LEVEL_KEY); else localStorage.setItem(LEVEL_KEY, arg);
      level = null;
      panel();
      void loadBody().catch((err) => { info.status = `Eroare: ${esc(String(err))}`; });
      return;
    }
    if (a === 'park') send({ type: 'park' });
    else if (a === 'drop1') send({ type: 'drop', height: 1 });
    else if (a === 'drop3') send({ type: 'drop', height: 3 });
    else if (a === 'crash50') send({ type: 'crash', kmh: 50 });
    else if (a === 'crash30') send({ type: 'crash', kmh: 30 });
    else if (a === 'torsion') { info.torsion = 'Test de torsiune în lucru…'; send({ type: 'torsion' }); }
    else if (a === 'slow') { slowmo = Number(arg); send({ type: 'slowmo', factor: slowmo }); }
    else if (a === 'show') {
      show[arg as keyof typeof show] = !show[arg as keyof typeof show];
      if (carGroup) carGroup.visible = show.model;
      if (nodeMesh) nodeMesh.visible = show.nodes;
      if (beamLines) beamLines.visible = show.beams;
    }
    panel();
  });

  return { open: () => { void open(); }, close };
}
